import { CurrencyTextField, NumberInput, TextInput } from '@core/components/Inputs';
import { SelectAsyncInput, SelectInput, SelectSimple } from '@core/components/Select';
import { Checkbox, Divider, FormControlLabel, Grid, Tooltip, Typography } from '@material-ui/core';
import SelectCstClassificacaoTributaria from 'app/components/Select/SelectCstClassificacaoTributaria';
import SelectCstSituacaoTributaria from 'app/components/Select/SelectCstSituacaoTributaria';
import SelectCtn from 'app/components/Select/SelectCtn';
import SelectNbs from 'app/components/Select/SelectNbs';
import empresaApi from 'app/services/api/empresaApi';
import cnaeApi from 'app/services/api/servicoFiscal/cnaeApi';
import lc116Api from 'app/services/api/servicoFiscal/lc116Api';
import lc116MunicipioSaoPaulo from 'app/services/api/servicoFiscal/lc116MunicipioSaoPaulo';
import { getEmpresaContexto } from 'app/utils/geralUtil';
import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { TipoExigibilidade } from '../enums';

const TabDadosFiscaisServico = () => {
  const formik = useFormikContext();
  const [empresaIbsCbs, setEmpresaIbsCbs] = useState(false);

  useEffect(() => {
    const carregarEmpresaIbsCbs = async () => {
      try {
        const empresaId = getEmpresaContexto();
        const empresa = await empresaApi.buscar(empresaId);
        const isEmpresaSimplesNacional = empresa?.data?.optanteSimples;
        const isEmpresaSaoPaulo = empresa?.data?.pessoa?.endereco?.cidade?.nome === 'São Paulo';
        const podeHabilitarIbsCbs = !isEmpresaSimplesNacional || (isEmpresaSimplesNacional && isEmpresaSaoPaulo);
        
        if (podeHabilitarIbsCbs && empresa?.data?.ibsCbs) {
          formik.values.fiscal.ibsCbs = true;
          setEmpresaIbsCbs(true);
        } else {
          setEmpresaIbsCbs(false);
        }
      } catch (error) {
        setEmpresaIbsCbs(false);
      }
    };
    carregarEmpresaIbsCbs();
    // Efeito de montagem: incluir as dependências reexecutaria a carga inicial a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { errors, touched, submitCount } = formik;

  const _errors = {
    valorIss: (submitCount || touched?.fiscal?.valorIss) && errors?.fiscal?.valorIss,
    valorIrrf: (submitCount || touched?.fiscal?.valorIrrf) && errors?.fiscal?.valorIrrf,
    valorPis: (submitCount || touched?.fiscal?.valorPis) && errors?.fiscal?.valorPis,
    valorCofins: (submitCount || touched?.fiscal?.valorCofins) && errors?.fiscal?.valorCofins,
    valorInss: (submitCount || touched?.fiscal?.valorInss) && errors?.fiscal?.valorInss,
    valorCsll: (submitCount || touched?.fiscal?.valorCsll) && errors?.fiscal?.valorCsll,
    cstSituacaoTributaria:
      (submitCount || touched?.fiscal?.cstSituacaoTributaria) && errors?.fiscal?.cstSituacaoTributaria,
    cstClassificacaoTributaria:
      (submitCount || touched?.fiscal?.cstClassificacaoTributaria) && errors?.fiscal?.cstClassificacaoTributaria,
    valorIbs: (submitCount || touched?.fiscal?.valorIbs) && errors?.fiscal?.valorIbs,
    valorIbsMunicipal: (submitCount || touched?.fiscal?.valorIbsMunicipal) && errors?.fiscal?.valorIbsMunicipal,
    valorCbs: (submitCount || touched?.fiscal?.valorCbs) && errors?.fiscal?.valorCbs,
    valorIs: (submitCount || touched?.fiscal?.valorIs) && errors?.fiscal?.valorIs,
    nbs: (submitCount || touched?.fiscal?.nbs) && errors?.fiscal?.nbs,
  };

  const retencaoIss = (tipoIss) => {
    return (
      <>
        {tipoIss === 'PERCENTUAL' && (
          <NumberInput
            id="fiscal.valorIss"
            label="ISS (%)"
            decimalScale={8}
            decimalSeparator=","
            allowNegative={false}
            isAllowed={(values) => {
              return !values.floatValue || values.floatValue <= 100;
            }}
            value={formik.values.fiscal?.valorIss}
            onChange={(event) => {
              formik.handleChange(event);
              if (event.target.value === undefined || event.target.value === '') {
                formik.setFieldValue('fiscal.issRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            touched={!!_errors?.valorIss}
            error={_errors?.valorIss}
          />
        )}

        {tipoIss === 'VALOR' && (
          <CurrencyTextField
            id="fiscal.valorIss"
            outputFormat="number"
            decimalCharacter=","
            digitGroupSeparator="."
            maximumValue="999999999"
            minimumValue="0"
            decimalPlaces={2}
            decimalPlacesShownOnBlur={2}
            decimalPlacesShownOnFocus={2}
            error={!!_errors?.valorIss}
            touched={!!_errors?.valorIss}
            helperText={_errors?.valorIss}
            onChange={(e, value) => {
              formik.handleChange({
                target: {
                  id: 'fiscal.valorIss',
                  value: e.target.value === '' ? '' : value,
                },
              });

              if (e.target.value === undefined || e.target.value === '') {
                formik.setFieldValue('fiscal.issRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            value={formik.values.fiscal?.valorIss}
            fullWidth
            variant="outlined"
            label="ISS (R$)"
            hideCurrencySymbol
          />
        )}
      </>
    );
  };

  const retencaoIrrf = (tipoIrrf) => {
    return (
      <>
        {tipoIrrf === 'PERCENTUAL' && (
          <NumberInput
            id="fiscal.valorIrrf"
            label="IRRF (%)"
            decimalScale={8}
            decimalSeparator=","
            allowNegative={false}
            isAllowed={(values) => {
              return !values.floatValue || values.floatValue <= 100;
            }}
            value={formik.values.fiscal?.valorIrrf}
            onChange={(event) => {
              formik.handleChange(event);
              if (event.target.value === undefined) {
                formik.setFieldValue('fiscal.irrfRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            touched={!!_errors?.valorIrrf}
            error={_errors?.valorIrrf}
          />
        )}

        {tipoIrrf === 'VALOR' && (
          <CurrencyTextField
            id="fiscal.valorIrrf"
            outputFormat="number"
            decimalCharacter=","
            digitGroupSeparator="."
            maximumValue="999999999"
            minimumValue="0"
            decimalPlaces={2}
            decimalPlacesShownOnBlur={2}
            decimalPlacesShownOnFocus={2}
            error={!!_errors?.valorIrrf}
            touched={!!_errors?.valorIrrf}
            helperText={_errors?.valorIrrf}
            onChange={(e, value) => {
              formik.handleChange({
                target: {
                  id: 'fiscal.valorIrrf',
                  value: e.target.value === '' ? '' : value,
                },
              });

              if (e.target.value === undefined || e.target.value === '') {
                formik.setFieldValue('fiscal.irrfRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            value={formik.values.fiscal?.valorIrrf}
            fullWidth
            variant="outlined"
            label="IRRF (R$)"
            hideCurrencySymbol
          />
        )}
      </>
    );
  };

  const retencaoPis = (tipoPis) => {
    return (
      <>
        {tipoPis === 'PERCENTUAL' && (
          <NumberInput
            id="fiscal.valorPis"
            label="PIS (%)"
            decimalScale={8}
            decimalSeparator=","
            allowNegative={false}
            isAllowed={(values) => {
              return !values.floatValue || values.floatValue <= 100;
            }}
            value={formik.values.fiscal?.valorPis}
            onChange={(event) => {
              formik.handleChange(event);
              if (event.target.value === undefined) {
                formik.setFieldValue('fiscal.pisRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            touched={!!_errors?.valorPis}
            error={_errors?.valorPis}
          />
        )}

        {tipoPis === 'VALOR' && (
          <CurrencyTextField
            id="fiscal.valorPis"
            outputFormat="number"
            decimalCharacter=","
            digitGroupSeparator="."
            maximumValue="999999999"
            minimumValue="0"
            decimalPlaces={2}
            decimalPlacesShownOnBlur={2}
            decimalPlacesShownOnFocus={2}
            error={!!_errors?.valorPis}
            touched={!!_errors?.valorPis}
            helperText={_errors?.valorPis}
            onChange={(e, value) => {
              formik.handleChange({
                target: {
                  id: 'fiscal.valorPis',
                  value: e.target.value === '' ? '' : value,
                },
              });

              if (e.target.value === undefined || e.target.value === '') {
                formik.setFieldValue('fiscal.pisRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            value={formik.values.fiscal?.valorPis}
            fullWidth
            variant="outlined"
            label="PIS (R$)"
            hideCurrencySymbol
          />
        )}
      </>
    );
  };

  const retencaoCofins = (tipoCofins) => {
    return (
      <>
        {tipoCofins === 'PERCENTUAL' && (
          <NumberInput
            id="fiscal.valorCofins"
            label="COFINS (%)"
            decimalScale={8}
            decimalSeparator=","
            allowNegative={false}
            isAllowed={(values) => {
              return !values.floatValue || values.floatValue <= 100;
            }}
            value={formik.values.fiscal?.valorCofins}
            onChange={(event) => {
              formik.handleChange(event);
              if (event.target.value === undefined) {
                formik.setFieldValue('fiscal.cofinsRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            touched={!!_errors?.valorCofins}
            error={_errors?.valorCofins}
          />
        )}

        {tipoCofins === 'VALOR' && (
          <CurrencyTextField
            id="fiscal.valorCofins"
            outputFormat="number"
            decimalCharacter=","
            digitGroupSeparator="."
            maximumValue="999999999"
            minimumValue="0"
            decimalPlaces={2}
            decimalPlacesShownOnBlur={2}
            decimalPlacesShownOnFocus={2}
            error={!!_errors?.valorCofins}
            touched={!!_errors?.valorCofins}
            helperText={_errors?.valorCofins}
            onChange={(e, value) => {
              formik.handleChange({
                target: {
                  id: 'fiscal.valorCofins',
                  value: e.target.value === '' ? '' : value,
                },
              });

              if (e.target.value === undefined || e.target.value === '') {
                formik.setFieldValue('fiscal.cofinsRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            value={formik.values.fiscal?.valorCofins}
            fullWidth
            variant="outlined"
            label="COFINS (R$)"
            hideCurrencySymbol
          />
        )}
      </>
    );
  };

  const retencaoInss = (tipoInss) => {
    return (
      <>
        {tipoInss === 'PERCENTUAL' && (
          <NumberInput
            id="fiscal.valorInss"
            label="INSS (%)"
            decimalScale={8}
            decimalSeparator=","
            allowNegative={false}
            isAllowed={(values) => {
              return !values.floatValue || values.floatValue <= 100;
            }}
            value={formik.values.fiscal?.valorInss}
            onChange={(event) => {
              formik.handleChange(event);
              if (event.target.value === undefined) {
                formik.setFieldValue('fiscal.inssRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            touched={!!_errors?.valorInss}
            error={_errors?.valorInss}
          />
        )}

        {tipoInss === 'VALOR' && (
          <CurrencyTextField
            id="fiscal.valorInss"
            outputFormat="number"
            decimalCharacter=","
            digitGroupSeparator="."
            maximumValue="999999999"
            minimumValue="0"
            decimalPlaces={2}
            decimalPlacesShownOnBlur={2}
            decimalPlacesShownOnFocus={2}
            error={!!_errors?.valorInss}
            touched={!!_errors?.valorInss}
            helperText={_errors?.valorInss}
            onChange={(e, value) => {
              formik.handleChange({
                target: {
                  id: 'fiscal.valorInss',
                  value: e.target.value === '' ? '' : value,
                },
              });

              if (e.target.value === undefined || e.target.value === '') {
                formik.setFieldValue('fiscal.inssRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            value={formik.values.fiscal?.valorInss}
            fullWidth
            variant="outlined"
            label="INSS (R$)"
            hideCurrencySymbol
          />
        )}
      </>
    );
  };

  const retencaoCsll = (tipoCsll) => {
    return (
      <>
        {tipoCsll === 'PERCENTUAL' && (
          <NumberInput
            id="fiscal.valorCsll"
            label="CSLL (%)"
            decimalScale={8}
            decimalSeparator=","
            allowNegative={false}
            isAllowed={(values) => {
              return !values.floatValue || values.floatValue <= 100;
            }}
            value={formik.values.fiscal?.valorCsll}
            onChange={(event) => {
              formik.handleChange(event);
              if (event.target.value === undefined) {
                formik.setFieldValue('fiscal.csllRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            touched={!!_errors?.valorCsll}
            error={_errors?.valorCsll}
          />
        )}

        {tipoCsll === 'VALOR' && (
          <CurrencyTextField
            id="fiscal.valorCsll"
            outputFormat="number"
            decimalCharacter=","
            digitGroupSeparator="."
            maximumValue="999999999"
            minimumValue="0"
            decimalPlaces={2}
            decimalPlacesShownOnBlur={2}
            decimalPlacesShownOnFocus={2}
            error={!!_errors?.valorCsll}
            touched={!!_errors?.valorCsll}
            helperText={_errors?.valorCsll}
            onChange={(e, value) => {
              formik.handleChange({
                target: {
                  id: 'fiscal.valorCsll',
                  value: e.target.value === '' ? '' : value,
                },
              });

              if (e.target.value === undefined || e.target.value === '') {
                formik.setFieldValue('fiscal.csllRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            value={formik.values.fiscal?.valorCsll}
            fullWidth
            variant="outlined"
            label="CSLL (R$)"
            hideCurrencySymbol
          />
        )}
      </>
    );
  };

  const retencaoIbs = (tipoIbs) => {
    return (
      <>
        {tipoIbs === 'PERCENTUAL' && (
          <NumberInput
            id="fiscal.valorIbs"
            label="IBS Estadual (%)"
            decimalScale={8}
            decimalSeparator=","
            allowNegative={false}
            isAllowed={(values) => {
              return !values.floatValue || values.floatValue <= 100;
            }}
            value={formik.values.fiscal?.valorIbs}
            onChange={(event) => {
              formik.handleChange(event);
              if (event.target.value === undefined) {
                formik.setFieldValue('fiscal.ibsRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            touched={!!_errors?.valorIbs}
            error={_errors?.valorIbs}
            required
          />
        )}

        {/* {tipoIbs === 'VALOR' && (
          <CurrencyTextField
            id="fiscal.valorIbs"
            outputFormat="number"
            decimalCharacter=","
            digitGroupSeparator="."
            maximumValue="999999999"
            minimumValue="0"
            decimalPlaces={2}
            decimalPlacesShownOnBlur={2}
            decimalPlacesShownOnFocus={2}
            error={!!_errors?.valorIbs}
            touched={!!_errors?.valorIbs}
            helperText={_errors?.valorIbs}
            onChange={(e, value) => {
              formik.handleChange({
                target: {
                  id: 'fiscal.valorIbs',
                  value: e.target.value === '' ? '' : value,
                },
              });

              if (e.target.value === undefined || e.target.value === '') {
                formik.setFieldValue('fiscal.ibsRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            value={formik.values.fiscal?.valorIbs}
            fullWidth
            variant="outlined"
            label="IBS (R$)"
            hideCurrencySymbol
            required
          />
        )} */}
      </>
    );
  };

  const retencaoIbsMunicipal = (tipoIbsMunicipal) => {
    return (
      <>
        {tipoIbsMunicipal === 'PERCENTUAL' && (
          <NumberInput
            id="fiscal.valorIbsMunicipal"
            label="IBS Municipal (%)"
            decimalScale={8}
            decimalSeparator=","
            allowNegative={false}
            isAllowed={(values) => {
              return !values.floatValue || values.floatValue <= 100;
            }}
            value={formik.values.fiscal?.valorIbsMunicipal}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            touched={!!_errors?.valorIbsMunicipal}
            error={_errors?.valorIbsMunicipal}
          />
        )}
      </>
    );
  };

  const retencaoCbs = (tipoCbs) => {
    return (
      <>
        {tipoCbs === 'PERCENTUAL' && (
          <NumberInput
            id="fiscal.valorCbs"
            label="CBS (%)"
            decimalScale={8}
            decimalSeparator=","
            allowNegative={false}
            isAllowed={(values) => {
              return !values.floatValue || values.floatValue <= 100;
            }}
            value={formik.values.fiscal?.valorCbs}
            onChange={(event) => {
              formik.handleChange(event);
              if (event.target.value === undefined) {
                formik.setFieldValue('fiscal.cbsRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            touched={!!_errors?.valorCbs}
            error={_errors?.valorCbs}
            required
          />
        )}

        {/* {tipoCbs === 'VALOR' && (
          <CurrencyTextField
            id="fiscal.valorCbs"
            outputFormat="number"
            decimalCharacter=","
            digitGroupSeparator="."
            maximumValue="999999999"
            minimumValue="0"
            decimalPlaces={2}
            decimalPlacesShownOnBlur={2}
            decimalPlacesShownOnFocus={2}
            error={!!_errors?.valorCbs}
            touched={!!_errors?.valorCbs}
            helperText={_errors?.valorCbs}
            onChange={(e, value) => {
              formik.handleChange({
                target: {
                  id: 'fiscal.valorCbs',
                  value: e.target.value === '' ? '' : value,
                },
              });

              if (e.target.value === undefined || e.target.value === '') {
                formik.setFieldValue('fiscal.cbsRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            value={formik.values.fiscal?.valorCbs}
            fullWidth
            variant="outlined"
            label="CBS (R$)"
            hideCurrencySymbol
            required
          />
        )} */}
      </>
    );
  };

  const retencaoIs = (tipoIs) => {
    return (
      <>
        {tipoIs === 'PERCENTUAL' && (
          <NumberInput
            id="fiscal.valorIs"
            label="IS (%)"
            decimalScale={8}
            decimalSeparator=","
            allowNegative={false}
            isAllowed={(values) => {
              return !values.floatValue || values.floatValue <= 100;
            }}
            value={formik.values.fiscal?.valorIs}
            onChange={(event) => {
              formik.handleChange(event);
              if (event.target.value === undefined) {
                formik.setFieldValue('fiscal.isRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            touched={!!_errors?.valorIs}
            error={_errors?.valorIs}
            required
          />
        )}

        {/* {tipoIs === 'VALOR' && (
          <CurrencyTextField
            id="fiscal.valorIs"
            outputFormat="number"
            decimalCharacter=","
            digitGroupSeparator="."
            maximumValue="999999999"
            minimumValue="0"
            decimalPlaces={2}
            decimalPlacesShownOnBlur={2}
            decimalPlacesShownOnFocus={2}
            error={!!_errors?.valorIs}
            touched={!!_errors?.valorIs}
            helperText={_errors?.valorIs}
            onChange={(e, value) => {
              formik.handleChange({
                target: {
                  id: 'fiscal.valorIs',
                  value: e.target.value === '' ? '' : value,
                },
              });

              if (e.target.value === undefined || e.target.value === '') {
                formik.setFieldValue('fiscal.isRetidoNaFonte', false);
              }
            }}
            onBlur={formik.handleBlur}
            value={formik.values.fiscal?.valorIs}
            fullWidth
            variant="outlined"
            label="IS (R$)"
            hideCurrencySymbol
            required
          />
        )} */}
      </>
    );
  };

  const campoCstClassificacaoTributaria = () => {
    const situacaoTributariaPreenchida = !!formik.values?.fiscal?.cstSituacaoTributaria?.id;

    const selectCstClassificacao = (
      <SelectCstClassificacaoTributaria
        id="fiscal.cstClassificacaoTributaria"
        name="fiscal.cstClassificacaoTributaria"
        label="Código de classificação tributária"
        value={formik.values?.fiscal?.cstClassificacaoTributaria}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        disabled={!formik.values?.fiscal?.cstSituacaoTributaria?.id}
        required
        idSituacao={formik.values?.fiscal?.cstSituacaoTributaria?.idSituacao}
        touched={!!_errors?.cstClassificacaoTributaria}
        error={_errors?.cstClassificacaoTributaria}
      />
    );

    if (situacaoTributariaPreenchida) {
      return selectCstClassificacao;
    }

    return (
      <Tooltip title="O campo será habilitado quando o código de situação tributária for informada" placement="top">
        <div style={{ width: '100%' }}>{selectCstClassificacao}</div>
      </Tooltip>
    );
  };

  return (
    <Grid container spacing={3} className="mt-16">
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <SelectAsyncInput
          id="fiscal.cnae"
          name="fiscal.cnae"
          label="CNAE do serviço"
          value={formik.values?.fiscal?.cnae}
          onChange={(event, value) => {
            formik.setFieldValue('fiscal.cnae.id', value?.id);
            formik.setFieldValue('fiscal.cnae.codigo', value?.codigo);
            formik.setFieldValue('fiscal.cnae.descricao', value?.descricao);
          }}
          getOptionKey={(option) => {
            return option?.id;
          }}
          getOptionLabel={(option) => {
            return option?.codigo && option?.descricao ? `${option.codigo} - ${option.descricao}` : '';
          }}
          fetchOnFocus
          fetchData={async (query = '', next = 0) => {
            if (typeof query === 'object') {
              query = '';
            }

            const response = await cnaeApi.pesquisarCnaesPorSubclasse({
              size: 100,
              sort: 'codigo',
              page: next,
              q: query,
            });

            const data = response?.data || [];

            return { data };
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <TextInput
          label="Código de serviço municipal"
          id="fiscal.codigoServicoMunicipal"
          value={formik.values?.fiscal?.codigoServicoMunicipal}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <SelectAsyncInput
          id="fiscal.lc116"
          name="fiscal.lc116"
          label="Código de Lei complementar 116"
          value={formik.values?.fiscal?.lc116}
          onChange={(event, value) => {
            formik.setFieldValue('fiscal.lc116.id', value?.id);
            formik.setFieldValue('fiscal.lc116.codigo', value?.codigo);
            formik.setFieldValue('fiscal.lc116.descricao', value?.descricao);
          }}
          getOptionKey={(option) => {
            return option?.id;
          }}
          getOptionLabel={(option) => {
            return option?.codigo && option?.descricao ? `${option.codigo} - ${option.descricao}` : '';
          }}
          fetchOnFocus
          fetchData={async (query = '', next = 0) => {
            if (typeof query === 'object') {
              query = '';
            }

            let response = null;

            const empresaId = getEmpresaContexto();
            const empresa = await empresaApi.buscar(empresaId);

            if (empresa.data?.pessoa?.endereco?.cidade?.nome === 'São Paulo') {
              response = await lc116MunicipioSaoPaulo.pesquisarLcs({
                size: 500,
                sort: 'codigo',
                page: next,
                q: query,
              });
            } else {
              response = await lc116Api.pesquisarLcs({
                size: 500,
                sort: 'codigo',
                page: next,
                q: query,
              });
            }

            const data = response?.data || [];

            return { data };
          }}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <SelectInput
          id="naturezaOperacao"
          label="Natureza da operação"
          value={formik.values?.naturezaOperacao}
          onChange={formik.handleChange}
          options={[
            { id: 'EXIGIVEL', title: TipoExigibilidade.EXIGIVEL },
            { id: 'NAO_INCIDENCIA', title: TipoExigibilidade.NAO_INCIDENCIA },
            { id: 'ISENCAO', title: TipoExigibilidade.ISENCAO },
            { id: 'EXPORTACAO', title: TipoExigibilidade.EXPORTACAO },
            { id: 'IMUNIDADE', title: TipoExigibilidade.IMUNIDADE },
            { id: 'EXI_SUS_DECI_JUDICIAL', title: TipoExigibilidade.EXI_SUS_DECI_JUDICIAL },
            { id: 'EXI_SUS_PROC_ADM', title: TipoExigibilidade.EXI_SUS_PROC_ADM },
          ]}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <SelectCtn
          id="fiscal.codigoTributacaoNacional"
          name="fiscal.codigoTributacaoNacional"
          label="Código Tributação Nacional - CTN"
          value={formik.values?.fiscal?.codigoTributacaoNacional}
          onChange={(event, value) => {
            formik.setFieldValue('fiscal.codigoTributacaoNacional.id', value?.id);
            formik.setFieldValue('fiscal.codigoTributacaoNacional.codigo', value?.codigo);
            formik.setFieldValue('fiscal.codigoTributacaoNacional.descricao', value?.descricao);
          }}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <SelectNbs
          id="fiscal.nbs"
          name="fiscal.nbs"
          value={formik.values?.fiscal?.nbs}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          label="Nomenclatura brasileira de serviços (NBS)"
          touched={!!_errors.nbs}
          error={_errors.nbs}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={1} xl={1}>
        <SelectSimple
          id="fiscal.tipoIss"
          label="Tipo"
          value={formik.values?.fiscal?.tipoIss}
          fullWidth
          onChange={(event) => {
            formik.handleChange(event);
            formik.setFieldValue('fiscal.valorIss', undefined);
            formik.setFieldValue('fiscal.issRetidoNaFonte', false);
          }}
          onBlur={formik.handleBlur}
          defaultValue="PERCENTUAL"
          options={[
            { title: '%', id: 'PERCENTUAL' },
            { title: 'R$', id: 'VALOR' },
          ]}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
        {retencaoIss(formik.values?.fiscal?.tipoIss)}
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              id="fiscal.issRetidoNaFonte"
              name="fiscal.issRetidoNaFonte"
              value={formik.values?.fiscal.issRetidoNaFonte}
              checked={formik.values?.fiscal.issRetidoNaFonte}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.values?.fiscal?.valorIss === undefined || formik.values?.fiscal?.valorIss === ''}
            />
          }
          label="Retenção de ISS pelo cliente"
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={1} xl={1}>
        <SelectSimple
          id="fiscal.tipoIrrf"
          label="Tipo"
          value={formik.values?.fiscal?.tipoIrrf}
          fullWidth
          onChange={(event) => {
            formik.handleChange(event);
            formik.setFieldValue('fiscal.valorIrrf', undefined);
            formik.setFieldValue('fiscal.irrfRetidoNaFonte', false);
          }}
          onBlur={formik.handleBlur}
          defaultValue="PERCENTUAL"
          options={[
            { title: '%', id: 'PERCENTUAL' },
            { title: 'R$', id: 'VALOR' },
          ]}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
        {retencaoIrrf(formik.values?.fiscal?.tipoIrrf)}
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              id="fiscal.irrfRetidoNaFonte"
              name="fiscal.irrfRetidoNaFonte"
              value={formik.values?.fiscal.irrfRetidoNaFonte}
              checked={formik.values?.fiscal.irrfRetidoNaFonte}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.values?.fiscal?.valorIrrf === undefined}
            />
          }
          label="Retenção de IRRF pelo cliente"
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={1} xl={1}>
        <SelectSimple
          id="fiscal.tipoPis"
          label="Tipo"
          value={formik.values?.fiscal?.tipoPis}
          fullWidth
          onChange={(event) => {
            formik.handleChange(event);
            formik.setFieldValue('fiscal.valorPis', undefined);
            formik.setFieldValue('fiscal.pisRetidoNaFonte', false);
          }}
          onBlur={formik.handleBlur}
          defaultValue="PERCENTUAL"
          options={[
            { title: '%', id: 'PERCENTUAL' },
            { title: 'R$', id: 'VALOR' },
          ]}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
        {retencaoPis(formik.values?.fiscal?.tipoPis)}
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              id="fiscal.pisRetidoNaFonte"
              name="fiscal.pisRetidoNaFonte"
              value={formik.values?.fiscal.pisRetidoNaFonte}
              checked={formik.values?.fiscal.pisRetidoNaFonte}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.values?.fiscal?.valorPis === undefined}
            />
          }
          label="Retenção de PIS pelo cliente"
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={1} xl={1}>
        <SelectSimple
          id="fiscal.tipoCofins"
          label="Tipo"
          value={formik.values?.fiscal?.tipoCofins}
          fullWidth
          onChange={(event) => {
            formik.handleChange(event);
            formik.setFieldValue('fiscal.valorCofins', undefined);
            formik.setFieldValue('fiscal.cofinsRetidoNaFonte', false);
          }}
          onBlur={formik.handleBlur}
          defaultValue="PERCENTUAL"
          options={[
            { title: '%', id: 'PERCENTUAL' },
            { title: 'R$', id: 'VALOR' },
          ]}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
        {retencaoCofins(formik.values?.fiscal?.tipoCofins)}
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              id="fiscal.cofinsRetidoNaFonte"
              name="fiscal.cofinsRetidoNaFonte"
              value={formik.values?.fiscal.cofinsRetidoNaFonte}
              checked={formik.values?.fiscal.cofinsRetidoNaFonte}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.values?.fiscal?.valorCofins === undefined}
            />
          }
          label="Retenção de COFINS pelo cliente"
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={1} xl={1}>
        <SelectSimple
          id="fiscal.tipoInss"
          label="Tipo"
          value={formik.values?.fiscal?.tipoInss}
          fullWidth
          onChange={(event) => {
            formik.handleChange(event);
            formik.setFieldValue('fiscal.valorInss', undefined);
            formik.setFieldValue('fiscal.inssRetidoNaFonte', false);
          }}
          onBlur={formik.handleBlur}
          defaultValue="PERCENTUAL"
          options={[
            { title: '%', id: 'PERCENTUAL' },
            { title: 'R$', id: 'VALOR' },
          ]}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
        {retencaoInss(formik.values?.fiscal?.tipoInss)}
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              id="fiscal.inssRetidoNaFonte"
              name="fiscal.inssRetidoNaFonte"
              value={formik.values?.fiscal.inssRetidoNaFonte}
              checked={formik.values?.fiscal.inssRetidoNaFonte}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.values?.fiscal?.valorInss === undefined}
            />
          }
          label="Retenção de INSS pelo cliente"
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={1} xl={1}>
        <SelectSimple
          id="fiscal.tipoCsll"
          label="Tipo"
          value={formik.values?.fiscal?.tipoCsll}
          fullWidth
          onChange={(event) => {
            formik.handleChange(event);
            formik.setFieldValue('fiscal.valorCsll', undefined);
            formik.setFieldValue('fiscal.csllRetidoNaFonte', false);
          }}
          onBlur={formik.handleBlur}
          defaultValue="PERCENTUAL"
          options={[
            { title: '%', id: 'PERCENTUAL' },
            { title: 'R$', id: 'VALOR' },
          ]}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
        {retencaoCsll(formik.values?.fiscal?.tipoCsll)}
      </Grid>

      <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              id="fiscal.csllRetidoNaFonte"
              name="fiscal.csllRetidoNaFonte"
              value={formik.values?.fiscal.csllRetidoNaFonte}
              checked={formik.values?.fiscal.csllRetidoNaFonte}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.values?.fiscal?.valorCsll === undefined}
            />
          }
          label="Retenção de CSLL pelo cliente"
        />
      </Grid>

      {empresaIbsCbs && (
        <>
          <Grid item xs={12}>
            <Typography variant="body1" style={{ fontWeight: 'bold' }}>
              IBS / CBS - Reforma Tributária
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={6}>
            <SelectCstSituacaoTributaria
              id="fiscal.cstSituacaoTributaria"
              name="fiscal.cstSituacaoTributaria"
              value={formik.values?.fiscal?.cstSituacaoTributaria}
              onChange={(e) => {
                formik.handleChange(e);
                formik.setFieldValue('fiscal.cstClassificacaoTributaria', null);
                formik.setFieldValue('fiscal.cstSituacaoTributaria.idSituacao', e.target.value?.idSituacao);
              }}
              onBlur={formik.handleBlur}
              label="Código de situação tributária (CST)"
              touched={!!_errors?.cstSituacaoTributaria}
              error={_errors?.cstSituacaoTributaria}
              required
            />
          </Grid>
          <Grid item xs={6}>
            {campoCstClassificacaoTributaria()}
          </Grid>

          {/* <Grid item xs={12} sm={12} md={12} lg={1} xl={1}>
      <SelectSimple
        id="fiscal.tipoIbs"
        label="Tipo"
        value={formik.values?.fiscal?.tipoIbs}
        fullWidth
        onChange={(event) => {
          formik.handleChange(event);
          formik.setFieldValue('fiscal.valorIbs', undefined);
          formik.setFieldValue('fiscal.ibsRetidoNaFonte', false);
        }}
        onBlur={formik.handleBlur}
        defaultValue="PERCENTUAL"
        options={[
          { title: '%', id: 'PERCENTUAL' },
          { title: 'R$', id: 'VALOR' },
        ]}
      />
    </Grid> */}
          <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
            {retencaoIbs(formik.values?.fiscal?.tipoIbs)}
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
            {retencaoIbsMunicipal(formik.values?.fiscal?.tipoIbsMunicipal || 'PERCENTUAL')}
          </Grid>
          {/* <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            id="fiscal.ibsRetidoNaFonte"
            name="fiscal.ibsRetidoNaFonte"
            value={formik.values?.fiscal.ibsRetidoNaFonte}
            checked={formik.values?.fiscal.ibsRetidoNaFonte}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.values?.fiscal?.valorIbs === undefined}
          />
        }
        label="Retenção de IBS pelo cliente"
      />
    </Grid>

    <Grid item xs={12} sm={12} md={12} lg={1} xl={1}>
      <SelectSimple
        id="fiscal.tipoCbs"
        label="Tipo"
        value={formik.values?.fiscal?.tipoCbs}
        fullWidth
        onChange={(event) => {
          formik.handleChange(event);
          formik.setFieldValue('fiscal.valorCbs', undefined);
          formik.setFieldValue('fiscal.cbsRetidoNaFonte', false);
        }}
        onBlur={formik.handleBlur}
        defaultValue="PERCENTUAL"
        options={[
          { title: '%', id: 'PERCENTUAL' },
          { title: 'R$', id: 'VALOR' },
        ]}
      />
    </Grid> */}
          <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
            {retencaoCbs(formik.values?.fiscal?.tipoCbs)}
          </Grid>
          {/* <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            id="fiscal.cbsRetidoNaFonte"
            name="fiscal.cbsRetidoNaFonte"
            value={formik.values?.fiscal.cbsRetidoNaFonte}
            checked={formik.values?.fiscal.cbsRetidoNaFonte}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.values?.fiscal?.valorCbs === undefined}
          />
        }
        label="Retenção de CBS pelo cliente"
      />
    </Grid>

    <Grid item xs={12} sm={12} md={12} lg={1} xl={1}>
      <SelectSimple
        id="fiscal.tipoIs"
        label="Tipo"
        value={formik.values?.fiscal?.tipoIs}
        fullWidth
        onChange={(event) => {
          formik.handleChange(event);
          formik.setFieldValue('fiscal.valorIs', undefined);
          formik.setFieldValue('fiscal.isRetidoNaFonte', false);
        }}
        onBlur={formik.handleBlur}
        defaultValue="PERCENTUAL"
        options={[
          { title: '%', id: 'PERCENTUAL' },
          { title: 'R$', id: 'VALOR' },
        ]}
      />
    </Grid> */}
          <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
            {retencaoIs(formik.values?.fiscal?.tipoIs)}
          </Grid>
          {/* <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            id="fiscal.isRetidoNaFonte"
            name="fiscal.isRetidoNaFonte"
            value={formik.values?.fiscal.isRetidoNaFonte}
            checked={formik.values?.fiscal.isRetidoNaFonte}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.values?.fiscal?.valorIs === undefined}
          />
        }
        label="Retenção de IS pelo cliente"
      />
    </Grid> */}
        </>
      )}
    </Grid>
  );
};

export default TabDadosFiscaisServico;
